import type { FC } from "react";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import cx from "classnames";

import type { Answer } from "../../../types/main";
import { isDropzone } from "../../../utils/isDropzone";

type Props = {
    answer: Answer;
    correct?: boolean;
    draggable?: boolean;
    dropzones: [string, number][];
    onDrop: (data: { answerId: number; column: number }) => void;
};

const ExerciseHouseAnswerFC: FC<Props> = ({ answer, correct, draggable = true, dropzones, onDrop }: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({});
    const style = useMemo(
        () => ({
            ...{
                left: 0,
                top: 0,
            },
            ...position,
        }),
        [position]
    );
    const onDrag = useCallback(
        (e) =>
            setPosition({
                height: 0,
                left: e.clientX,
                opacity: 0,
                position: "fixed",
                top: e.clientY,
                transform: "none",
                width: 0,
            }),
        []
    );
    const onDragEnd = useCallback((e) => setPosition({}), []);
    const onDragStart = useCallback(
        (e) => {
            e.dataTransfer.setData("id", answer.id);
        },
        [answer.id]
    );

    const onTouchMove = useCallback(
        (e) => {
            const { clientX, clientY } = e.touches[0];
            (window as any).dropzone = dropzones.find(([id]) => isDropzone(clientX, clientY, id));

            if (ref.current) {
                setPosition({
                    height: ref.current.clientHeight,
                    left: clientX - ref.current.clientWidth / 2,
                    opacity: 1,
                    position: "fixed",
                    top: clientY - ref.current.clientHeight / 2,
                    transform: "none",
                    width: ref.current.clientWidth,
                });
            }
        },
        [dropzones]
    );
    const onTouchEnd = useCallback(() => {
        const dropzone = (window as any).dropzone as [string, number] | undefined;
        if (dropzone) {
            onDrop({
                answerId: answer.id,
                column: dropzone[1],
            });
        }
        (window as any).dropzone = undefined;

        setPosition({});
    }, [answer.id, onDrop]);

    return (
        <div
            className={cx("exercise-house__answer", {
                "exercise-house__answer--correct": correct,
                "exercise-house__answer--wrong": correct === false,
            })}
        >
            {answer.text}
            <div
                className="exercise-house__answer-draggable"
                draggable={draggable}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onDragStart={onDragStart}
                onTouchEnd={onTouchEnd}
                onTouchMove={onTouchMove}
                ref={ref}
                style={style}
            />
        </div>
    );
};

export const ExerciseHouseAnswer = memo(ExerciseHouseAnswerFC);
